import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {getExpertsWithUid as getStaticExpertsWithUid} from '../../../../experts/expertsData';
import {subscribeToExperts} from '../../../../../firebase/firestore/experts';
import {resolveExpertAvatarUrl} from '../../../../../utils/expertAvatar';
import {useSession} from '../../../../../firebase/UserProvider';
import {useReportStore} from '../../../useReportStore';
import {extractPlainText} from './utils/contentExtractor';
import {callLLMStreaming} from '../../../../../utils/llmStreamingApi';
import AnalystResponseContent from './AnalystResponseContent';
import ReasoningBox from '../../../disclosureEditorPage/leftPanel/ReasoningBox';

const BOTTOM_EPSILON = 64;

const AnalystPerspectives = () => {
    const [question, setQuestion] = useState('');
    const [selectedAnalyst, setSelectedAnalyst] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isReasoning, setIsReasoning] = useState(false);
    const [response, setResponse] = useState('');
    const [reasoningSteps, setReasoningSteps] = useState([]);
    const [error, setError] = useState(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    // Experts with UID — needed for analyst personas; seeded with static data
    const [allExperts, setAllExperts] = useState(getStaticExpertsWithUid());

    const {user} = useSession();
    const editorView = useReportStore(s => s.editorView);
    
    // Extract ICR fields from report store
    const informationRequirement = useReportStore(s => s.informationRequirement);
    const eei = useReportStore(s => s.eei);
    const backgroundJustification = useReportStore(s => s.backgroundJustification);
    const targetInformation = useReportStore(s => s.targetInformation);
    const constraintsSpecialInstructions = useReportStore(s => s.constraintsSpecialInstructions);

    const scrollRef = useRef(null);
    const contentRef = useRef(null);

    // Subscribe to Firestore experts and keep the list up to date
    useEffect(() => {
        return subscribeToExperts((firestoreExperts) => {
            const withUid = (firestoreExperts || []).filter(e => e.uid).slice(0, 3);
            if (withUid.length > 0) {
                setAllExperts(withUid);
            }
        });
    }, []);

    // Filter out the current user from the analysts list
    const analysts = allExperts.filter(analyst => analyst.uid !== user?.uid);

    const analystAvatarUrl = useCallback(
        (analyst) => resolveExpertAvatarUrl(analyst),
        []
    );

    const scrollToBottom = useCallback((useSmooth = false) => {
        const container = scrollRef.current;
        if (!container) return;
        if (useSmooth) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        } else {
            // Direct assignment for instant scroll (streaming, resize)
            container.scrollTop = container.scrollHeight;
        }
    }, []);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Consider within BOTTOM_EPSILON of bottom as "at bottom"
        const atBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < BOTTOM_EPSILON;
        setIsAtBottom(atBottom);
    }, []);

    const handleAnalystChange = (event) => {
        const analystUid = event.target.value;
        setSelectedAnalyst(analystUid);
    };

    const handleSubmit = async () => {
        const analyst = analysts.find(a => a.uid === selectedAnalyst);

        // Extract content in multiple formats
        // const {plainText, markdown, json} = extractAllFormats(editorView);
        const plainText = extractPlainText(editorView);

        // Build ICR JSON structure
        const icrData = {};
        
        if (informationRequirement) {
            icrData.informationRequirement = informationRequirement;
        }
        
        if (eei) {
            const eeiParts = [];
            if (eei.what) eeiParts.push(`What: ${eei.what}`);
            if (eei.where) eeiParts.push(`Where: ${eei.where}`);
            if (eei.when) eeiParts.push(`When: ${eei.when}`);
            if (eei.detail) eeiParts.push(`Detail: ${eei.detail}`);
            
            if (eeiParts.length > 0) {
                icrData.eei = eeiParts.join('\n');
            }
        }
        
        if (backgroundJustification) {
            icrData.backgroundJustification = backgroundJustification;
        }
        
        if (targetInformation) {
            const targetInfo = {};
            if (targetInformation.targetNameTypeActivity || targetInformation.nameTypeActivity) {
                targetInfo.nameTypeActivity = targetInformation.targetNameTypeActivity || targetInformation.nameTypeActivity;
            }
            if (targetInformation.geographicArea) {
                targetInfo.geographicArea = targetInformation.geographicArea;
            }
            if (targetInformation.timeOfInterest) {
                targetInfo.timeOfInterest = targetInformation.timeOfInterest;
            }
            
            if (Object.keys(targetInfo).length > 0) {
                icrData.targetInformation = targetInfo;
            }
        }
        
        if (constraintsSpecialInstructions) {
            icrData.constraintsSpecialInstructions = constraintsSpecialInstructions;
        }

        const payload = {
            reportContent: plainText,
            question,
            expert: analyst,
            icr: icrData
        };

        const messages = [
            {
                role: 'user',
                content: JSON.stringify(payload)
            }
        ];

        const promptId = 'pmpt_69821d68db108193a09c0e3add24b89301d3ff64f7730ea6';

        setIsGenerating(true);
        setIsReasoning(false);
        setResponse('');
        setReasoningSteps([]);
        setError(null);

        try {
            await callLLMStreaming(
                promptId,
                messages,
                (event) => {
                    switch (event.type) {
                        case 'started':
                            setIsGenerating(true);
                            break;
                        case 'reasoning_started':
                            setIsReasoning(true);
                            setReasoningSteps(event.reasoningSteps || []);
                            break;
                        case 'reasoning':
                            setIsReasoning(true);
                            setReasoningSteps(event.reasoningSteps || []);
                            break;
                        case 'reasoning_done':
                            setIsReasoning(false);
                            setReasoningSteps(event.reasoningSteps || []);
                            break;
                        case 'output':
                            setResponse(event.outputText || '');
                            if (event.reasoningSteps?.length) {
                                setReasoningSteps(event.reasoningSteps);
                            }
                            break;
                        case 'output_done':
                        case 'completed':
                            setResponse(event.outputText || '');
                            if (event.reasoningSteps?.length) {
                                setReasoningSteps(event.reasoningSteps);
                            }
                            setIsGenerating(false);
                            setIsReasoning(false);
                            break;
                        case 'error':
                            console.error('LLM streaming error:', event.error);
                            setError(event.error || 'An error occurred');
                            setIsGenerating(false);
                            setIsReasoning(false);
                            break;
                        default:
                            break;
                    }
                }
            );
        } catch (error) {
            console.error('Error calling LLM:', error);
            setError(error.message || 'An error occurred');
            setIsGenerating(false);
            setIsReasoning(false);
        }
    };

    // Watch content for height changes (streaming content growing)
    useEffect(() => {
        const container = scrollRef.current;
        const content = contentRef.current;
        if (!container || !content || typeof ResizeObserver === 'undefined') return;

        const ro = new ResizeObserver(() => {
            if (isAtBottom) {
                // Keep stuck to bottom while streaming or at bottom
                container.scrollTop = container.scrollHeight;
            }
        });

        ro.observe(content);
        return () => ro.disconnect();
    }, [isAtBottom]);

    // Auto-scroll to bottom when content changes during streaming
    useEffect(() => {
        if (isGenerating && isAtBottom) {
            scrollToBottom(false);
        }
    }, [response, reasoningSteps, isGenerating, isAtBottom, scrollToBottom]);

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            spacing={2}
            p={2}
            sx={{
                overflowY: 'auto',
                height: '100%',
                minHeight: 0,
                flex: 1,
            }}
        >
            <Stack ref={contentRef} spacing={2}>
                <Alert severity="info" sx={{mb: 2}}>
                    SME activity within Minsky is analyzed to create a synthetic projection of analyst opinions.
                    Make sure to always confirm with the actual SME!
                </Alert>

                <FormControl fullWidth>
                    <InputLabel id="analyst-select-label">Choose Analyst</InputLabel>
                    <Select
                        labelId="analyst-select-label"
                        id="analyst-select"
                        value={selectedAnalyst}
                        label="Choose Analyst"
                        onChange={handleAnalystChange}
                        variant="outlined"
                        renderValue={(value) => {
                            const analyst = analysts.find(a => a.uid === value);
                            if (!analyst) return '';
                            return (
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar
                                        src={analystAvatarUrl(analyst)}
                                        alt={analyst.displayName}
                                        sx={{ width: 32, height: 32 }}
                                    >
                                        {analyst.displayName?.[0]}
                                    </Avatar>
                                    <Stack>
                                        <Typography variant="body1">
                                            {analyst.displayName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {analyst.title} - {analyst.office}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            );
                        }}
                    >
                        {analysts.map((analyst) => (
                            <MenuItem key={analyst.uid} value={analyst.uid}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                    <Avatar
                                        src={analystAvatarUrl(analyst)}
                                        alt={analyst.displayName}
                                        sx={{ width: 40, height: 40 }}
                                    >
                                        {analyst.displayName?.[0]}
                                    </Avatar>
                                    <Stack>
                                        <Typography variant="body1">
                                            {analyst.displayName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {analyst.title} - {analyst.office}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Question
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Ask your question"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        variant="outlined"
                    />
                </Box>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!selectedAnalyst || !question.trim() || isGenerating}
                >
                    {isGenerating ? 'Generating...' : 'Submit'}
                </Button>

                {/* Reasoning Box - shows before/during response generation */}
                {(reasoningSteps.length > 0 || isReasoning) && (
                    <ReasoningBox
                        reasoningSteps={reasoningSteps.length ? reasoningSteps : ['Thinking...']}
                        isActive={isReasoning}
                    />
                )}

                {/* Response display */}
                {(response || error) && !isReasoning && (
                    <Box sx={{mt: 2}}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Response:
                        </Typography>
                        <AnalystResponseContent markdown={response} error={error}/>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
};

export default AnalystPerspectives;
